import { useState, useEffect, useCallback, useRef } from "react";
import * as tf from "@tensorflow/tfjs";

const MODEL_KEY    = "indexeddb://digitair-turnaround-model";
const VOCAB_KEY    = "digitair-turnaround-vocab";
const METRICS_KEY  = "digitair-turnaround-metrics";
const MAX_TURNAROUND = 300;
export const TURNAROUND_EPOCHS = 80;

function buildFeature(record, vocab) {
  const { airports, acTypes } = vocab;
  const aptIdx = airports.indexOf(record.airport);
  const acIdx  = acTypes.indexOf(record.acType);
  return [
    aptIdx < 0 ? 0 : aptIdx / Math.max(airports.length - 1, 1),
    acIdx  < 0 ? 0 : acIdx  / Math.max(acTypes.length  - 1, 1),
    record.ataHour   / 23,
    record.ataMinute / 59,
    record.dayOfWeek / 6,
    record.month     / 11,
  ];
}

function buildModel() {
  const model = tf.sequential({
    layers: [
      tf.layers.dense({ inputShape: [6], units: 24, activation: "relu" }),
      tf.layers.dropout({ rate: 0.1 }),
      tf.layers.dense({ units: 12, activation: "relu" }),
      tf.layers.dense({ units: 1,  activation: "sigmoid" }),
    ],
  });
  model.compile({ optimizer: tf.train.adam(0.01), loss: "meanSquaredError", metrics: ["mae"] });
  return model;
}

export function useTurnaroundModel() {
  const [model, setModel]               = useState(null);
  const [isModelReady, setIsModelReady] = useState(false);
  const [isTraining, setIsTraining]     = useState(false);
  const [trainingLogs, setTrainingLogs] = useState([]);
  const [vocab, setVocab]               = useState(null);
  const [lastMetrics, setLastMetrics]   = useState(null);
  const lastLogRef                      = useRef(null);
  const workerRef                       = useRef(null);

  useEffect(() => {
    const loadSaved = async () => {
      try {
        const savedVocab   = localStorage.getItem(VOCAB_KEY);
        const savedMetrics = localStorage.getItem(METRICS_KEY);
        if (!savedVocab) return;
        const m = await tf.loadLayersModel(MODEL_KEY);
        setModel(m);
        setVocab(JSON.parse(savedVocab));
        if (savedMetrics) setLastMetrics(JSON.parse(savedMetrics));
        setIsModelReady(true);
      } catch (err) {
        console.warn("Turnaround model: failed to load from storage:", err);
      }
    };
    loadSaved();
    return () => { workerRef.current?.terminate(); };
  }, []);

  const trainModel = useCallback(async (rawData) => {
    if (!rawData || rawData.length < 10) return false;
    setIsTraining(true);
    setTrainingLogs([]);

    const airports = [...new Set(rawData.map((r) => r.airport))].sort();
    const acTypes  = [...new Set(rawData.map((r) => r.acType))].sort();
    const newVocab = { airports, acTypes };

    const features = rawData.map((r) => buildFeature(r, newVocab));
    const labels   = rawData.map((r) => [Math.min(r.turnaroundMinutes, MAX_TURNAROUND) / MAX_TURNAROUND]);

    return new Promise((resolve) => {
      workerRef.current?.terminate();
      // eslint-disable-next-line no-undef
      const worker = new Worker(new URL('./workers/tfTrainWorker.js', import.meta.url));
      workerRef.current = worker;

      worker.onmessage = async ({ data: msg }) => {
        if (msg.type === 'epoch') {
          lastLogRef.current = msg.payload;
          setTrainingLogs((prev) => [...prev, msg.payload]);
          return;
        }
        worker.terminate();
        workerRef.current = null;
        setIsTraining(false);

        if (msg.type === 'error') {
          console.error("Turnaround model worker error:", msg.payload.message);
          resolve(false);
          return;
        }

        const m       = buildModel();
        const weights = msg.payload.weightSpecs.map(({ data, shape, dtype }) =>
          tf.tensor(data, shape, dtype)
        );
        m.setWeights(weights);
        weights.forEach((w) => w.dispose());

        setVocab(newVocab);
        setModel(m);
        setIsModelReady(true);

        const metrics = lastLogRef.current
          ? { val_loss: lastLogRef.current.val_loss, mae: lastLogRef.current.mae }
          : null;
        if (metrics) setLastMetrics(metrics);

        let saved = false;
        try {
          localStorage.removeItem(VOCAB_KEY);
          localStorage.removeItem(METRICS_KEY);
          try { await tf.io.removeModel(MODEL_KEY); } catch {}
          await m.save(MODEL_KEY);
          localStorage.setItem(VOCAB_KEY, JSON.stringify(newVocab));
          if (metrics) localStorage.setItem(METRICS_KEY, JSON.stringify(metrics));
          saved = true;
        } catch (err) {
          console.error("Turnaround model: save failed:", err);
        }
        resolve(saved);
      };

      worker.onerror = (err) => {
        console.error("Turnaround model worker crash:", err);
        worker.terminate();
        workerRef.current = null;
        setIsTraining(false);
        resolve(false);
      };

      worker.postMessage({
        type: 'train',
        payload: { modelType: 'turnaround', features, labels, epochs: TURNAROUND_EPOCHS, batchSize: 32, validationSplit: 0.2 },
      });
    });
  }, []);

  const predict = useCallback(
    (inputFeatures) => {
      if (!model || !vocab) return null;
      const feature = buildFeature(inputFeatures, vocab);
      const input   = tf.tensor2d([feature]);
      const output  = model.predict(input);
      const raw     = output.dataSync();
      input.dispose();
      output.dispose();
      return Math.max(15, Math.round(raw[0] * MAX_TURNAROUND));
    },
    [model, vocab]
  );

  const resetModel = useCallback(async () => {
    workerRef.current?.terminate();
    workerRef.current = null;
    try { await tf.io.removeModel(MODEL_KEY); } catch {}
    localStorage.removeItem(VOCAB_KEY);
    localStorage.removeItem(METRICS_KEY);
    setModel(null);
    setVocab(null);
    setLastMetrics(null);
    setIsModelReady(false);
    setIsTraining(false);
    setTrainingLogs([]);
    lastLogRef.current = null;
  }, []);

  return {
    isModelReady,
    isTraining,
    trainingLogs,
    lastMetrics,
    vocab,
    trainModel,
    predict,
    resetModel,
  };
}
